import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { formatPrice, resolveMoney } from "@/utils/formatPrice";
import { format, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { sv } from "date-fns/locale";
import { ArrowLeft, Download, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MatchiSettledRow from "@/components/admin/MatchiSettledRow";
import { splitMatchiPayments } from "@/lib/matchiRevenue";

type TypeFilter = "all" | "booking" | "membership";
type PeriodKey = "this_month" | "last_month";

export default function DashboardRevenue() {
  const { t } = useTranslation();
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [period, setPeriod] = useState<PeriodKey>("this_month");

  const periodDates = useMemo(() => {
    const base = period === "last_month" ? subMonths(new Date(), 1) : new Date();
    return {
      start: format(startOfMonth(base), "yyyy-MM-dd"),
      end: format(endOfMonth(base), "yyyy-MM-dd"),
      label: format(base, "MMMM yyyy", { locale: sv }),
    };
  }, [period]);

  // Fetch all booking payments for the period
  const { data: payments = [], isLoading } = useQuery({
    queryKey: ["dashboard-revenue", periodDates.start, periodDates.end],
    queryFn: async () => {
      // Sprint D aggregates: include amount_minor + currency.
      const { data, error } = await supabase
        .from("booking_payments")
        .select(
          "id, booking_id, amount, amount_minor, currency, status, payment_method, payment_method_id, payment_type, paid_at, created_at, user_id, profiles:user_id(full_name)"
        )
        .gte("created_at", periodDates.start + "T00:00:00")
        .lte("created_at", periodDates.end + "T23:59:59")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  // Compute revenue breakdown — Sprint D aggregates: prefer amount_minor.
  const breakdown = useMemo(() => {
    const sumMinor = (arr: typeof payments) =>
      arr.reduce((sum, p) => sum + (resolveMoney((p as any).amount_minor, p.amount, (p as any).currency)?.valueMinor ?? 0), 0);
    // C3/BES-004: MATCHi-avräknade betalningar är inte Boply-intäkt — pengarna togs
    // emot av MATCHi. De särredovisas nedan istället för att blandas in i totalen.
    const { boply: paidPayments, matchi: matchiPaid } = splitMatchiPayments(
      payments.filter((p) => p.status === "paid"),
    );
    const matchiSettledMinor = sumMinor(matchiPaid);
    const totalRevenueMinor = sumMinor(paidPayments);
    const bookingRevenueMinor = sumMinor(paidPayments.filter((p) => p.payment_type === "booking" || p.payment_type === "full"));
    const membershipRevenueMinor = sumMinor(paidPayments.filter((p) => p.payment_type === "membership"));
    const otherRevenueMinor = totalRevenueMinor - bookingRevenueMinor - membershipRevenueMinor;
    const pendingMinor = sumMinor(payments.filter((p) => p.status === "pending"));
    const refundedMinor = sumMinor(payments.filter((p) => p.status === "refunded"));

    return {
      totalRevenueMinor,
      matchiSettledMinor,
      bookingRevenueMinor,
      membershipRevenueMinor,
      otherRevenueMinor,
      pendingMinor,
      refundedMinor,
      bookingPct: totalRevenueMinor > 0 ? Math.round((bookingRevenueMinor / totalRevenueMinor) * 100) : 0,
      membershipPct: totalRevenueMinor > 0 ? Math.round((membershipRevenueMinor / totalRevenueMinor) * 100) : 0,
      otherPct: totalRevenueMinor > 0 ? Math.round((otherRevenueMinor / totalRevenueMinor) * 100) : 0,
    };
  }, [payments]);

  // Filter transactions
  const filtered = payments.filter((p) => {
    if (typeFilter === "all") return true;
    if (typeFilter === "booking")
      return p.payment_type === "booking" || p.payment_type === "full";
    if (typeFilter === "membership") return p.payment_type === "membership";
    return true;
  });

  function exportCSV() {
    const header = ["Datum", "Kund", "Typ", "Belopp", "Metod", "Status"];
    const rows = filtered.map((p) => {
      const profile = p.profiles as unknown as { full_name: string | null } | null;
      return [
        format(new Date(p.created_at), "yyyy-MM-dd HH:mm"),
        profile?.full_name ?? "—",
        p.payment_type,
        String(p.amount),
        p.payment_method ?? "—",
        p.status,
      ];
    });
    const csv = [header, ...rows].map((r) => r.join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `intakter-${periodDates.start}.csv`; // ASCII-safe för filnamn
    a.click();
    URL.revokeObjectURL(url);
  }

  const typeFilters: { key: TypeFilter; label: string }[] = [
    { key: "all", label: t("common.all", "Alla") },
    { key: "booking", label: t("dashboard.bookings", "Bokningar") },
    { key: "membership", label: t("dashboard.memberships", "Medlemskap") },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/manager/dashboard"
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <h1 className="text-2xl font-bold">
          {t("dashboard.revenue", "Intäkter")} — {periodDates.label}
        </h1>
      </div>

      {/* Revenue breakdown card */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t("dashboard.revenueBreakdown", "Intäkter per kategori")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-3xl font-bold">
                {formatPrice({ valueMinor: breakdown.totalRevenueMinor, currency: "SEK" })}
              </div>

              {/* Booking revenue */}
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span>{t("dashboard.courtBookings", "Banbokning")}</span>
                  <span>
                    {formatPrice({ valueMinor: breakdown.bookingRevenueMinor, currency: "SEK" })} ({breakdown.bookingPct}%)
                  </span>
                </div>
                <Progress value={breakdown.bookingPct} className="h-2" />
              </div>

              {/* Membership revenue */}
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span>{t("dashboard.memberships", "Medlemskap")}</span>
                  <span>
                    {formatPrice({ valueMinor: breakdown.membershipRevenueMinor, currency: "SEK" })} ({breakdown.membershipPct}%)
                  </span>
                </div>
                <Progress value={breakdown.membershipPct} className="h-2" />
              </div>

              {/* Other */}
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span>{t("dashboard.other", "Övrigt")}</span>
                  <span>
                    {formatPrice({ valueMinor: breakdown.otherRevenueMinor, currency: "SEK" })} ({breakdown.otherPct}%)
                  </span>
                </div>
                <Progress value={breakdown.otherPct} className="h-2" />
              </div>

              <MatchiSettledRow
                valueMinor={breakdown.matchiSettledMinor}
                className="border-t pt-3 text-sm text-muted-foreground"
              />

              <div className="border-t pt-3 flex gap-6 text-sm text-muted-foreground">
                <span>
                  {t("dashboard.pending", "Väntande")}:{" "}
                  {formatPrice({ valueMinor: breakdown.pendingMinor, currency: "SEK" })}
                </span>
                <span>
                  {t("dashboard.refunded", "Återbetalt")}:{" "}
                  {formatPrice({ valueMinor: breakdown.refundedMinor, currency: "SEK" })}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Transactions */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <CardTitle className="text-base">
                  {t("dashboard.transactions", "Transaktioner")}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Select
                    value={period}
                    onValueChange={(v) => setPeriod(v as PeriodKey)}
                  >
                    <SelectTrigger className="w-[160px] h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="this_month">
                        {t("dashboard.thisMonth", "Denna månad")}
                      </SelectItem>
                      <SelectItem value="last_month">
                        {t("dashboard.lastMonth", "Förra månaden")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={exportCSV}>
                    <Download className="h-4 w-4 mr-1" />
                    CSV
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Type filters */}
              <div className="flex gap-2 mb-4 flex-wrap">
                {typeFilters.map((f) => (
                  <Button
                    key={f.key}
                    variant={typeFilter === f.key ? "default" : "outline"}
                    size="sm"
                    onClick={() => setTypeFilter(f.key)}
                  >
                    {f.label}
                  </Button>
                ))}
              </div>

              {/* Transaction list */}
              {filtered.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  {t("dashboard.noTransactions", "Inga transaktioner")}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="pb-2 font-medium">
                          {t("common.date", "Datum")}
                        </th>
                        <th className="pb-2 font-medium">
                          {t("common.customer", "Kund")}
                        </th>
                        <th className="pb-2 font-medium">
                          {t("common.type", "Typ")}
                        </th>
                        <th className="pb-2 font-medium text-right">
                          {t("common.amount", "Belopp")}
                        </th>
                        <th className="pb-2 font-medium">
                          {t("common.status", "Status")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((p) => {
                        const profile = p.profiles as unknown as {
                          full_name: string | null;
                        } | null;
                        return (
                          <tr key={p.id} className="border-b last:border-0">
                            <td className="py-2">
                              {format(new Date(p.created_at), "d MMM HH:mm", {
                                locale: sv,
                              })}
                            </td>
                            <td className="py-2">
                              {profile?.full_name ?? "—"}
                            </td>
                            <td className="py-2 capitalize">{p.payment_type}</td>
                            <td className="py-2 text-right font-medium">
                              {formatPrice(resolveMoney((p as any).amount_minor, p.amount, (p as any).currency) ?? { valueMinor: 0, currency: "SEK" })}
                            </td>
                            <td className="py-2">
                              <Badge
                                variant={
                                  p.status === "paid"
                                    ? "default"
                                    : p.status === "refunded"
                                    ? "secondary"
                                    : "destructive"
                                }
                                className={
                                  p.status === "paid"
                                    ? "bg-green-100 text-green-800 hover:bg-green-100"
                                    : undefined
                                }
                              >
                                {p.status === "paid"
                                  ? t("common.paid", "Betald")
                                  : p.status === "refunded"
                                  ? t("dashboard.refunded", "Återbetald")
                                  : t("common.unpaid", "Obetald")}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
